import auth0 from 'auth0-js'
import _ from 'lodash'
import { AUTH0_AUDIENCE, AUTH0_CALLBACK_URL, AUTH0_CLIENT_ID, AUTH0_DOMAIN,
  AUTH0_NAMESPACE, } from '../constants'
import history from '../history'

export default class Auth {

  userProfile
  tokenRenewalTimeout

  auth0 = new auth0.WebAuth({
    audience: AUTH0_AUDIENCE,
    domain: AUTH0_DOMAIN,
    clientID: AUTH0_CLIENT_ID,
    redirectUri: AUTH0_CALLBACK_URL,
    responseType: 'token id_token',
    scope: 'openid profile groups roles'
  })

  constructor() {
    this.login = this.login.bind(this)
    this.logout = this.logout.bind(this)
    this.handleAuthentication = this.handleAuthentication.bind(this)
    this.isAuthenticated = this.isAuthenticated.bind(this)
    this.getAccessToken = this.getAccessToken.bind(this)
    this.getProfile = this.getProfile.bind(this)
    this.scheduleRenewal()
  }

  login() {
    this.auth0.authorize()
  }

  handleAuthentication() {
    this.auth0.parseHash(async (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        await this.setSession(authResult)
        history.replace('/')
      } else if (err) {
        history.replace('/')
        console.error(err)
        throw err
      }
    })
  }

  async setSession(authResult) {

    // Set the time that the access token will expire at
    let expiresAt = JSON.stringify(
      authResult.expiresIn * 1000 + new Date().getTime()
    )

    const { idTokenPayload } = authResult
    localStorage.setItem('access_token', authResult.accessToken)
    localStorage.setItem('expires_at', expiresAt)
    localStorage.setItem('groups', _.get(idTokenPayload, AUTH0_NAMESPACE + 'groups', []).join(','))
    localStorage.setItem('roles', _.get(idTokenPayload, AUTH0_NAMESPACE + 'roles', []).join(','))

    // schedule a token renewal
    this.scheduleRenewal()

    // navigate to the home route
    history.replace('/')
  }

  getAccessToken() {
    const accessToken = localStorage.getItem('access_token')
    if (!accessToken) {
      throw new Error('No access token found')
    }
    return accessToken
  }

  getProfile() {
    return new Promise((resolve,reject) => {
      let accessToken = this.getAccessToken()
      this.auth0.client.userInfo(accessToken, (err, profile) => {
        if (err) reject(err)
        this.userProfile = profile
        resolve(profile)
      })
    })
  }

  logout() {
    // Clear access token and ID token from local storage
    localStorage.removeItem('access_token')
    localStorage.removeItem('expires_at')
    localStorage.removeItem('groups')
    localStorage.removeItem('roles')
    this.userProfile = null
    clearTimeout(this.tokenRenewalTimeout)
    this.auth0.logout()
    // navigate to the home route
    history.replace('/')
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    let expiresAt = JSON.parse(localStorage.getItem('expires_at'))
    return new Date().getTime() < expiresAt
  }

  getGroups() {
    const groups = localStorage.getItem('groups')
    return groups ? groups.split(',') : []
  }

  hasGroup(group) {
    return group ? this.getGroups().indexOf(group) > -1 : this.getGroups().length > 0
  }

  getRoles() {
    const roles = localStorage.getItem('roles')
    return roles ? roles.split(',') : []
  }

  hasRole(role) {
    return role ? this.getRoles().indexOf(role) > -1 : this.getRoles().length > 0
  }

  isUser() {
    return this.hasRole('user')
  }

  renewToken() {
    this.auth0.checkSession({},
      (err, result) => {
        if (err) {
          console.error(err)
          throw err
        } else {
          this.setSession(result)
          // DEBUG && console.log(`Successfully renewed auth!`)
        }
      }
    )
  }

  scheduleRenewal() {
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'))
    const delay = expiresAt - Date.now()
    if (delay > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewToken()
      }, delay)
    }
  }
}
